import { MutableRefObject, useRef } from "react";
import { Link } from "react-router-dom";
import { styled } from "@monolith-forensics/monolith-ui";
import { DismissableBanner } from "@/Features/_components/DismissableBanner";
import Loader from "@/components/Loader";
import type { Evidence } from "@/Features/Evidence/evidence.types";
import { CaseFilesProvider } from "@/Features/Files/Hooks/CaseFilesContext";
import type { CaseFilesTableRef } from "@/Features/Files/Tables/CaseFilesTable";
import { FILE_SOURCE_TYPES } from "@/Features/Files/files.types";
import {
  EVIDENCE_FILES_BANNER_ID,
  EVIDENCE_FILES_HELP_URL,
  caseFilesFolderPath,
} from "./evidence-files.constants";
import { useEvidenceItemFolder } from "./evidence-files.query";
import { EvidenceFilesTable } from "./EvidenceFilesTable";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;

  .evidence-files-banner {
    margin-bottom: 10px;
  }
`;

/**
 * The evidence item's Files tab (ENG-2156): its folder under Evidence Files
 * in Case Files, shown as the Case Files table. Only rendered with the
 * evidence_files_in_case_files flag on, since the folder lookup reads the
 * managed tree the Files API hides while the flag is off.
 */
export const EvidenceFilesContent: React.FC<{ evidenceInfo: Evidence }> = ({
  evidenceInfo,
}) => {
  const { evidence_id, case_id, case_uuid } = evidenceInfo;
  const tableRef = useRef<CaseFilesTableRef>();

  const {
    data: folder,
    isLoading,
    isError,
    error,
  } = useEvidenceItemFolder({ evidence_id, case_uuid });

  if (isLoading) return <Loader />;
  if (isError) {
    return (
      <div style={{ marginTop: 20 }}>
        Unable to load files
        {error instanceof Error && error.message ? `: ${error.message}` : "."}
      </div>
    );
  }

  const folderPath = folder ? caseFilesFolderPath(case_id, folder.uuid) : null;

  return (
    <StyledContainer>
      <DismissableBanner
        className="evidence-files-banner"
        id={EVIDENCE_FILES_BANNER_ID}
        title="Upload and manage this evidence item's files here, including photos"
        data-testid="evidence-files-banner"
        actions={
          <>
            {folderPath && (
              <Link
                to={folderPath}
                data-testid="evidence-files-open-in-case-files-link"
              >
                See in Case Files
              </Link>
            )}
            <a
              href={EVIDENCE_FILES_HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Learn more about evidence files"
            >
              Learn more
            </a>
          </>
        }
      >
        {folder
          ? "These files are also in Case Files, and images show in the Photos tab too."
          : "Once you upload files, you'll also see them in Case Files, and images will show up in the Photos tab too."}{" "}
        A change in one place shows up in the others.
      </DismissableBanner>
      <CaseFilesProvider
        case_id={case_id}
        case_uuid={case_uuid}
        tableRef={tableRef as MutableRefObject<CaseFilesTableRef>}
        folder_id={folder?.uuid}
        uploadTarget={{
          source_type: FILE_SOURCE_TYPES.EVIDENCE_ITEM_FOLDER,
          source_id: evidence_id,
        }}
        flat
        surface="evidence-files-tab"
      >
        <EvidenceFilesTable
          evidenceInfo={evidenceInfo}
          folder={folder ?? null}
        />
      </CaseFilesProvider>
    </StyledContainer>
  );
};

export default EvidenceFilesContent;
